import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldX, ArrowLeft, Home, Loader2 } from 'lucide-react';
import { buildPathWithPreviewToken } from '@/lib/previewEnvironment';
import { useAuth } from '@/contexts/AuthContext';
import { getHomeRouteForRole } from '@/lib/authHomeRoute';

export default function AccessDeniedPage() {
  const navigate = useNavigate();
  const { user, role, signOut, loading } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const authPath = buildPathWithPreviewToken('/auth');
  const safeFallbackPath = buildPathWithPreviewToken(
    user ? getHomeRouteForRole(role) : '/auth',
  );

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(authPath, { replace: true });
    }
  }, [loading, user, navigate, authPath]);

  const handleGoHome = () => {
    if (isRedirecting) return;
    navigate(safeFallbackPath, { replace: true });
  };

  const handleGoToLogin = async () => {
    if (isRedirecting) return;
    setIsRedirecting(true);

    if (user) {
      await signOut();
    }

    navigate(authPath, { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Redirecionando para login…</p>
        <a
          href={authPath}
          className="text-primary underline text-sm"
          onClick={(e) => {
            e.preventDefault();
            window.location.href = authPath;
          }}
        >
          Ir para login
        </a>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-destructive/5 p-4">
      <Card className="w-full max-w-md shadow-xl border-destructive/20">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-2">
            <ShieldX className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl font-bold text-destructive">Acesso Negado</CardTitle>
          <CardDescription className="text-base">
            Você não tem permissão para acessar esta página.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            Se você acredita que deveria ter acesso, entre em contato com o administrador do sistema.
          </p>

          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={handleGoHome}
              disabled={isRedirecting}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para o início
            </Button>

            <Button
              className="w-full"
              onClick={handleGoToLogin}
              disabled={isRedirecting}
            >
              <Home className="mr-2 h-4 w-4" />
              {user ? 'Sair e ir para o Login' : 'Ir para o Login'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
